/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { LoaderButton } from 'components/ProgressButton';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { styles } from 'routes/renewMember/styles/renewMemberFileCard.styles';
import { useRenewSubscriptionWithCardOnFileMutation } from 'store/api/version-api.slice';
import { showMessage } from 'store/slices/message.slice';

import RENEW_MEMBER_FILE_CARD_SCHEMA from './renewMemberFileCard.schema';

const INITIAL_VALUES = {
	member_type: '',
	card_on_file: ''
};

const RenewMemberFileCard = ({ setPaymentStatus, renewInfo = {}, params }) => {
	const dispatch = useDispatch();

	const [memberTypes, cardsOnFile, memberInstance] = useMemo(() => {
		let _memberTypes = [];
		let _cardsOnFile = [];
		let _memberInstance = {};

		if (renewInfo.user?.member_types) {
			_memberTypes = [...renewInfo.user.member_types];
		}

		if (renewInfo.card_details) {
			_cardsOnFile = [...renewInfo.card_details];
		}

		if (renewInfo.member) {
			_memberInstance = {
				...renewInfo.member
			};
		}

		return [_memberTypes, _cardsOnFile, _memberInstance];
	}, [renewInfo]);

	const [renewSubscriptionWithCardOnFile, { isLoading: isRenewLoading }] =
		useRenewSubscriptionWithCardOnFileMutation();

	const dispatchMessage = ({ message, variant = 'success' }) =>
		dispatch(
			showMessage({
				message,
				variant
			})
		);

	const renewWithCardOnFile = async data => {
		try {
			const renewData = {
				email: memberInstance.email,
				member_type: data.member_type,
				sq_customer_id: memberInstance.sq_customer_id,
				sq_customer_card_id: data.card_on_file.id,
				sq_postal_code: data.card_on_file.billing_address.postal_code
			};

			const result = await renewSubscriptionWithCardOnFile({
				portalId: params.portal_id,
				contactId: params.contact_id,
				data: renewData
			});

			if (result?.data.status === 'COMPLETED') {
				const { currency, amount } = result.data.total_money;
				const format_amount = amount / 100;
				const message = `Your payment of ${format_amount} ${currency} has been processed successfully`;

				dispatchMessage({ message });
				setPaymentStatus('SUCCESS');
			} else {
				dispatchMessage({
					message: 'Payment Failed! Please, try again later',
					variant: 'error'
				});
				setPaymentStatus('ERROR');
			}
		} catch (e) {
			dispatchMessage({
				message: 'Payment Failed! Please, try again later',
				variant: 'error'
			});
			setPaymentStatus('ERROR');
		}
	};

	const { handleSubmit, control } = useForm({
		defaultValues: INITIAL_VALUES,
		resolver: yupResolver(RENEW_MEMBER_FILE_CARD_SCHEMA)
	});

	const renderSelectedCard = value => {
		if (!value) return '';
		return `${value.card_brand} ${value.last_4}`;
	};

	return (
		<Paper elevation={2} sx={styles.card}>
			<CardHeader
				disableTypography
				title={
					<Typography component={'span'} variant="h6" color="primary">
						Renew Member with Card on File
					</Typography>
				}
			/>

			<CardContent sx={styles.content}>
				<form onSubmit={handleSubmit(renewWithCardOnFile)}>
					<label style={{ display: 'none' }}>
						HubspotCollectedFormsWorkaround
						https://community.hubspot.com/t5/APIs-Integrations/How-to-stop-collected-forms-from-collecting-a-form/m-p/299172#M28102
					</label>
					<input name="credit-card" style={{ display: 'none' }} required="" id="credit-card"></input>
					<Controller
						name="member_type"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<FormControl variant="filled" sx={styles.field} error={Boolean(error)}>
								<InputLabel id="select-member-type" required>
									Select Renewal Member Type
								</InputLabel>
								<Select labelId="select-member-type" {...field}>
									{memberTypes.map(type => {
										return (
											<MenuItem key={type.id} value={type.member_type}>
												{type.member_type}
											</MenuItem>
										);
									})}
								</Select>
								{Boolean(error?.message) && <Box sx={styles.error}>{error.message}</Box>}
							</FormControl>
						)}
					/>

					<Controller
						name="card_on_file"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<FormControl variant="filled" error={Boolean(error)} sx={styles.field}>
								<InputLabel id="select-member-type" required>
									Select Card on File
								</InputLabel>
								<Select
									labelId="select-member-type"
									id="demo-simple-select-filled"
									{...field}
									renderValue={renderSelectedCard}
								>
									{cardsOnFile.map(card => {
										return (
											<MenuItem key={card.id} value={card}>
												{`${card.card_brand} ${card.last_4}`}
											</MenuItem>
										);
									})}
								</Select>
								{Boolean(error?.message) && <Box sx={styles.error}>{error.message}</Box>}
							</FormControl>
						)}
					/>

					<LoaderButton
						type="submit"
						variant="contained"
						color="primary"
						circleColor="secondary"
						loading={isRenewLoading}
					>
						Renew with Card on File
					</LoaderButton>
				</form>
			</CardContent>
		</Paper>
	);
};

export default RenewMemberFileCard;
